import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { PRODUCT_CATEGORIES } from "../data/catalog";

const PRODUCTS_MENU = PRODUCT_CATEGORIES.map((c) => ({
  to: `/products/category/${c.slug}`,
  label: c.name,
  desc: c.tagline,
}));

const SOLUTIONS_MENU = [
  { to: "/products?segment=B2C", label: "Residential", desc: "Homes, villas & garden pods" },
  { to: "/products/category/commercial-pods", label: "Commercial", desc: "Offices, retail & F&B" },
  { to: "/hospitality", label: "Hospitality", desc: "Resorts, retreats & glamping" },
  { to: "/products?segment=B2B", label: "Developers", desc: "Scale inventory, faster ROI" },
];

const RESOURCES_MENU = [
  { to: "/resources", label: "Brochure", desc: "Download the NUVUN catalogue" },
  { to: "/resources#catalogues", label: "Catalogues", desc: "Per-collection spec sheets" },
  { to: "/projects", label: "Case Studies", desc: "Delivered projects across India" },
  { to: "/resources#faqs", label: "FAQs", desc: "Pricing, timelines & approvals" },
];

const MENUS = { products: PRODUCTS_MENU, solutions: SOLUTIONS_MENU, resources: RESOURCES_MENU };

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMenu(null);
  }, [loc.pathname, loc.search]);

  const DropTrigger = ({ id, label }) => (
    <button
      data-testid={`nav-drop-${id}`}
      onMouseEnter={() => setMenu(id)}
      onClick={() => setMenu((m) => (m === id ? null : id))}
      className={`eyebrow inline-flex items-center gap-1.5 py-2 transition-colors ${
        menu === id ? "text-[#D9A441]" : "text-[#F2EDE3]/75 hover:text-white"
      }`}
    >
      {label}
      <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${menu === id ? "rotate-180" : ""}`} />
    </button>
  );

  return (
    <>
      <header
        data-testid="site-navbar"
        onMouseLeave={() => setMenu(null)}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#0A0908]/95 backdrop-blur-xl ${
          scrolled || menu ? "border-b border-[#D9A441]/20 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.9)]" : "border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 flex items-center justify-between h-[76px]">
          <Link to="/" data-testid="navbar-logo-link" aria-label="Nuvun home" className="shrink-0">
            <Logo inverted height={34} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" onMouseEnter={() => {}}>
            <DropTrigger id="products" label="Products" />
            <DropTrigger id="solutions" label="Solutions" />
            <NavLink
              to="/projects"
              data-testid="nav-link-projects"
              onMouseEnter={() => setMenu(null)}
              className={({ isActive }) =>
                `eyebrow py-2 transition-colors ${isActive ? "text-[#D9A441]" : "text-[#F2EDE3]/75 hover:text-white"}`
              }
            >
              Projects
            </NavLink>
            <NavLink
              to="/about"
              data-testid="nav-link-about"
              onMouseEnter={() => setMenu(null)}
              className={({ isActive }) =>
                `eyebrow py-2 transition-colors ${isActive ? "text-[#D9A441]" : "text-[#F2EDE3]/75 hover:text-white"}`
              }
            >
              About
            </NavLink>
            <DropTrigger id="resources" label="Resources" />
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/resources" data-testid="navbar-brochure-cta" className="btn-ghost !px-5 !py-3">
              <FileText className="w-3.5 h-3.5" />
              Download Brochure
            </Link>
            <Link to="/contact" data-testid="navbar-start-project-cta" className="btn-gold !px-6 !py-3">
              Start Your Project
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-[#F2EDE3]"
            aria-label="Open menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Desktop dropdown */}
        <AnimatePresence>
          {menu && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block absolute left-0 right-0 top-[76px] bg-[#0E0D0A]/95 backdrop-blur-xl border-b border-[#D9A441]/15"
              data-testid={`nav-dropdown-${menu}`}
            >
              <div className="max-w-[1440px] mx-auto px-10 lg:px-14 py-8 grid grid-cols-3 gap-x-10 gap-y-2">
                {MENUS[menu].map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    data-testid={`nav-menu-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-start justify-between gap-4 py-3.5 px-4 -mx-4 rounded-md hover:bg-[#D9A441]/[0.07] transition-colors"
                  >
                    <span>
                      <span className="block font-display text-lg text-[#F2EDE3] group-hover:text-[#D9A441] transition-colors">{item.label}</span>
                      <span className="block text-xs text-[#F2EDE3]/50 mt-1">{item.desc}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 mt-1.5 text-[#D9A441] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile overlay */}
      <div
        data-testid="mobile-nav-overlay"
        className={`fixed inset-0 z-40 lg:hidden bg-[#0A0908] text-white transition-all duration-500 overflow-y-auto ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="h-[76px]" />
        <div className="px-7 py-8 pb-16">
          <div className="eyebrow text-[#D9A441] mb-3">Products</div>
          <div className="grid grid-cols-1 gap-0 mb-8">
            {PRODUCTS_MENU.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-display text-2xl py-3 border-b border-white/10 text-[#F2EDE3] flex items-center justify-between"
              >
                {l.label}
                <ArrowRight className="w-4 h-4 text-[#D9A441]" />
              </Link>
            ))}
          </div>

          <div className="eyebrow text-[#D9A441] mb-3">Explore</div>
          <div className="grid grid-cols-2 gap-x-6">
            {[
              { to: "/products", label: "All Products" },
              { to: "/hospitality", label: "Hospitality" },
              { to: "/projects", label: "Projects" },
              { to: "/about", label: "About" },
              { to: "/sustainability", label: "Sustainability" },
              { to: "/resources", label: "Resources" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-body text-sm py-3 border-b border-white/10 text-[#F2EDE3]/80"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <Link to="/contact" data-testid="mobile-nav-start-project" className="btn-gold w-full">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/resources" data-testid="mobile-nav-brochure" className="btn-ghost w-full">
              <FileText className="w-4 h-4" /> Download Brochure
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
